import {Component, Input, Output, OnInit, OnDestroy, ViewChild, EventEmitter} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {EcommerceOrder, Item} from "../../../model/EcommerceOrder";
import {activeOrderRequestItems, isIgnoredOrderRequestItem, ORDER_REQUEST_ITEM_STATUS_IGNORE} from "./orderRequestItemIgnore";
import {Product} from "../../../model/Product";
import {ProductService} from "../../service-data/product.service";
import {EcommerceOrderService} from "../../service-data/ecommerce-order.service";
import {EcommerceProductService} from "../../service-data/ecommerce-product.service";
import {EcommerceProduct} from "../../../model/EcommerceProduct";
import {FacilityInventoryProductService} from "../../service-data/facility-inventory-product.service";
import {FacilityInventoryProduct} from "../../../model/FacilityInventoryProduct";
import {ProductCode, ProductMatchPreference} from "../csv/csv-page/ProductCode";
import {Address} from "../../../model/Address";
import {AddressValidationOrder} from "../../../model/AddressValidationOrder";
import {
  buildCarrierServiceLevelOptions,
  CarrierServiceLevelOption,
  CarrierServiceLevelOptionSource
} from "../../util/carrier-service-level-options";

// interface CarrierServiceLevel {
//   value: string;
//   viewValue: string;
// }

@Component({
  selector: 'app-ecommerce-order-details',
  templateUrl: './ecommerce-order-details.component.html',
  styleUrls: ['./ecommerce-order-details.component.css'],
  providers: [DatePipe]
})
export class EcommerceOrderDetailsComponent implements OnInit, OnDestroy {
  @Input() ecommerceOrder: EcommerceOrder | null = null;
  @Input() narrowDisplay: boolean = false;
  @Output() editingOrder = new EventEmitter<boolean>();
  @ViewChild('searchInput') searchInput: any;

  addressValidationOrder: AddressValidationOrder;
  protected readonly JSON = JSON;

  /**
   * `OrderRequestItem.status` value meaning "the operator dismissed this line".
   * Re-exported from `orderRequestItemIgnore.ts` so the template and this class
   * share one definition with the readiness predicate.
   */
  public static readonly ITEM_STATUS_IGNORE = ORDER_REQUEST_ITEM_STATUS_IGNORE;

  /**
   * Whether `MANUAL_OVERRIDE` may be sent on `EcommerceOrder.addressStatus`.
   *
   * ## Why this is a switch and not just the right value
   *
   * The server confirmed on 2026-09-23 that `MANUAL_OVERRIDE` is the correct
   * value for an address an operator took responsibility for: its
   * `isAddressStatusUsable()` already treats it as shippable, and its "stuck
   * order" sweep already excludes it. It is strictly better than `SUCCESS`,
   * which claims a validator stood behind an address when none did.
   *
   * But it had never been written by anything and was **not declared on the
   * column**. An undeclared enum value in MySQL is either rejected outright or
   * silently stored as `''` -- which reads back as not-usable and would hold
   * the order with no explanation. That is a worse failure than the overstated
   * `SUCCESS`, and it would land on exactly the orders an operator had just
   * rescued.
   *
   * The migration (`sql/add_ecommerceorder_addressstatus_values.sql`) shipped
   * with that reply, and the server asked us to hold until they confirm it is
   * applied in the environment under test. See
   * docs/contracts/inbox/_processed/2026-09-23-address-precedence-confirmed-manual-override-legal-and-validationresult-free-text.md
   *
   * **Flip this to `true` once that confirmation arrives -- nothing else needs
   * to change.** Until then an overridden address ships as `SUCCESS` and is
   * distinguishable only by `bookorder.addressValidationOverride`, which the
   * server does read.
   */
  public static readonly MANUAL_OVERRIDE_ADDRESS_STATUS_AVAILABLE = false;



  itemSelectionData: ProductCode | undefined = undefined;
  items: Item[] = [];
  displayedColumns: string[] = ['ecommerceProduct', 'product', 'quantity', 'deleteItem'];
  editingQuantity: Item | null = null;
  editingProduct: Item | null = null;
  itemQuantityForm = new FormControl(1, [Validators.required, Validators.min(1)]);
  // productSelectionForm = new FormControl<Product | null>(null);

  carrierServiceLevelOptions: CarrierServiceLevelOption[] = [];

  /**
   * How far `buildCarrierServiceLevelOptions()` had to degrade to keep the
   * combo box populated. Drives the hint under the box, because how far the
   * prices beside each entry can be trusted differs by source.
   */
  carrierServiceLevelOptionsSource: CarrierServiceLevelOptionSource = 'FACILITY';

  customerEditMode = false;
  customerForm: FormGroup;

  carrierServiceEditMode = false;
  carrierServiceForm = new FormControl('');
  // availableCarrierServices: CarrierServiceLevel[] = [
  //   { value: 'UPS-Standard', viewValue: 'UPS - Standard' },
  //   { value: 'FedEx-Ground', viewValue: 'FedEx - Ground' },
  //   // Add more options as needed
  // ];
  originalCarrierService: string = '';

  products: Product[] = [];
  filteredProducts: Product[] = [];

  /**
   * What the operator has typed into the search box inside the product
   * dropdown.
   *
   * Held here rather than read off the input element because the box is
   * projected into the mat-select overlay panel: the `@ViewChild` for it is
   * undefined whenever the panel is closed, so `clearSearch()` used to write
   * `.value` onto an `ElementRef` (or onto `undefined`) and the text never
   * actually cleared. The input is bound with `[value]="productSearchText"`,
   * which makes this property the single source of truth in both directions.
   */
  productSearchText = '';

  productsLoaded = 0;

  /**
   * Memo for `getProduct()`. That method is called straight from the template,
   * so it runs on every change-detection pass for every row; without this the
   * fallback scan of `products` would be O(rows x catalog) per pass.
   * Cleared whenever the catalog is reloaded.
   */
  private productById: { [id: string]: Product } = {};

  ecommerceProducts: EcommerceProduct[] = [];
  ecommerceProductsLoaded = 0;

  facilityInventoryProducts: FacilityInventoryProduct[] = [];
  facilityInventoryProductsByProductIdAndFacility: any = {};
  /**
   * `${org_ID}_${facilityId}` of the load currently held, or `''` for none.
   * The inventory query is per-facility, so the org alone does not identify it.
   */
  facilityInventoryProductsLoaded = '';

  private ngUnsubscribe = new Subject<void>();
  // private addressValidated: boolean = false;
  saveProductSelectionToEcommerceProduct: Boolean = true;

  constructor(private datePipe: DatePipe, public productService: ProductService, public facilityInventoryProductService: FacilityInventoryProductService, private ecommerceOrderService: EcommerceOrderService, public ecommerceProductService: EcommerceProductService) {
    this.customerForm = new FormGroup({
      title: new FormControl(''),
      firstname: new FormControl(''),
      lastname: new FormControl(''),
      company: new FormControl(''),
      address1: new FormControl(''),
      address2: new FormControl(''),
      address3: new FormControl(''),
      address4: new FormControl(''),
      city: new FormControl(''),
      state: new FormControl(''),
      postalcode: new FormControl(''),
      country: new FormControl(''),
      phonecountry: new FormControl(''),
      phone: new FormControl(''),
      email: new FormControl('')
    });
  }

  ngOnInit(): void {
    this.loadProductsAndEcommerceProducts();
    this.setup();
  }

  setup() {
    if (this.ecommerceOrder) {
      // if ( this.ecommerceOrder.addressStatus === 'SUCCESS') {
      //   this.addressValidated = true;
      // }
      // Fix the possible lack of attachment of an EcommerceProduct even when there is an ecommerceProductId
      for ( const i of this.ecommerceOrder.getItems() ) {
        if ( i.ecommerceProductId && !i.ecommerceProduct && this.ecommerceProductService.objectsById['' + i.ecommerceProductId + ''] ) {
          i.ecommerceProduct = this.ecommerceProductService.objectsById['' + i.ecommerceProductId + ''];
        }
      }
      this.items = this.ecommerceOrder.getItems();
      this.customerForm.patchValue({
        title: this.ecommerceOrder.title,
        firstname: this.ecommerceOrder.firstname,
        lastname: this.ecommerceOrder.lastname,
        company: this.ecommerceOrder.company,
        address1: this.ecommerceOrder.address1,
        address2: this.ecommerceOrder.address2,
        address3: this.ecommerceOrder.address3,
        address4: this.ecommerceOrder.address4,
        city: this.ecommerceOrder.city,
        state: this.ecommerceOrder.state,
        postalcode: this.ecommerceOrder.postalcode,
        country: this.ecommerceOrder.country,
        phonecountry: this.ecommerceOrder.phonecountry,
        phone: this.ecommerceOrder.phone,
        email: this.ecommerceOrder.email
      });
      this.originalCarrierService = this.ecommerceOrder.shippingInfo;
      this.carrierServiceForm.setValue(this.originalCarrierService);

    }
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Load the three catalogs this panel needs, scoped to the order on screen.
   *
   * ## Every result is re-filtered locally, and that is not belt-and-braces
   *
   * `AbstractLiveDataService.getFilteredItems(filter, reset)` does **not** hand
   * back the rows matching `filter`. It merges what it fetches into the
   * service-wide `objects` array and emits *that* -- every strategy inside it
   * ends with `return this.objects`. And when `reset` is false (which is what
   * every call here passes) and the service has already loaded once, the filter
   * is never put on the wire at all: the method short-circuits to
   * `return this.objectsObservable`, i.e. the whole accumulated cache.
   *
   * These services are app-wide singletons. Under the `network` role, which
   * ranges over every organization and every EcommerceConnection, that cache
   * holds rows from other orgs and other connections -- put there by the
   * unfiltered orders list, by a previously-opened order, or by a live
   * notification push. Assigning the emitted array straight to `this.products` /
   * `this.ecommerceProducts` therefore leaked another tenant's catalog into this
   * order's product picker, and another connection's mappings into the
   * `remoteProductId` lookup in `saveProductEdit()`.
   *
   * Passing `reset: true` is not the fix -- it would throw away a cache the rest
   * of the app shares and force a refetch every time an order is opened. So the
   * scoping is simply re-applied here, on the way in.
   */
  loadProductsAndEcommerceProducts() {
    if ( !this.ecommerceOrder || !this.ecommerceOrder.ecommerceConnection ) {
      this.productsLoaded = 0;
      this.ecommerceProductsLoaded = 0;
      this.facilityInventoryProductsLoaded = '';
      return;
    }

    const orgId = this.ecommerceOrder.ecommerceConnection.org_ID;
    const connectionId = this.ecommerceOrder.ecommerceConnection.ID;
    const facilityId = this.ecommerceOrder.bookorder ? this.ecommerceOrder.bookorder.facilityId : 0;

    if ( this.productsLoaded !== orgId ) {
      this.productService.getFilteredItems({ eq: { 'product.org_ID': orgId, 'product.archived': 'FALSE', 'product.isProof': 'FALSE' }}, false).subscribe(products => {
        this.products = this.productsForOrg(products, orgId);
        this.filteredProducts = this.products.slice();
        this.productById = {};
        this.productsLoaded = orgId;
      });
    }

    // EcommerceProducts are scoped to a *connection*, not to an org. This used to
    // filter `ecommerceconnection_ID` by `org_ID`, so it asked for the rows of
    // some unrelated connection (usually none at all). Together with the
    // whole-cache return described above, the existing-row lookup in
    // `saveProductEdit()` could miss the row that does exist and fall through to
    // the "create a new one" branch -- which the server rejects with a duplicate
    // key on (ecommerceconnection_ID, product_ID, remoteproduct_ID) -- or hit a
    // row owned by a *different* connection and silently re-point that
    // connection's mapping.
    if ( this.ecommerceProductsLoaded !== connectionId ) {
      this.ecommerceProductService.getFilteredItems({ eq: { 'ecommerceProduct.ecommerceconnection_ID': connectionId } }, false).subscribe(ecommerceProducts => {
        this.ecommerceProducts = this.ecommerceProductsForConnection(ecommerceProducts, connectionId);
        this.ecommerceProductsLoaded = connectionId;
      });
    }

    // Keyed by facility as well as org, because the query is per-facility. Keyed
    // on `org_ID` alone, two orders in the same org shipping from different
    // facilities counted as "already loaded" for each other, so the second one
    // kept the first one's inventory.
    const facilityInventoryKey = `${orgId}_${facilityId}`;
    if ( this.facilityInventoryProductsLoaded !== facilityInventoryKey ) {
      this.facilityInventoryProductService.getFilteredItems({ eq: { 'facilityInventoryProduct.product.org_ID': orgId, 'facilityInventoryProduct.printnode_ID': facilityId } }, false).subscribe(facilityInventoryProducts => {
        this.facilityInventoryProducts = ( facilityInventoryProducts || [] ).filter( facilityInventoryProduct => Number(facilityInventoryProduct.printnode_ID) === Number(facilityId) );
        // Rebuilt rather than added to: it is a cache of one facility's stock and
        // must not carry entries left from the last order that was open.
        this.facilityInventoryProductsByProductIdAndFacility = {};
        for ( const facilityInventoryProduct of this.facilityInventoryProducts ) {
          this.facilityInventoryProductsByProductIdAndFacility[facilityInventoryProduct.printnode_ID + '_' + facilityInventoryProduct.product_ID ] = facilityInventoryProduct;
          // Could iterate over the list of items in the productIds field
        }
        this.facilityInventoryProductsLoaded = facilityInventoryKey;
      });
    }
  }

  /**
   * The subset of `products` that this order's organization may actually use.
   *
   * Mirrors the server-side filter that was requested, because the service layer
   * does not guarantee it was applied -- see `loadProductsAndEcommerceProducts()`.
   */
  private productsForOrg(products: Product[], orgId: number): Product[] {
    if ( !products ) {
      return [];
    }
    return products.filter( product => Number(product.org_ID) === Number(orgId)
      && String(product.archived) !== 'TRUE'
      && String(product.isProof) !== 'TRUE' );
  }

  /**
   * The subset of `ecommerceProducts` belonging to this order's connection.
   *
   * `saveProductEdit()` searches this list by `remoteproduct_ID`, which is only
   * unique *within* a connection, so an unscoped list means writing to another
   * connection's row.
   */
  private ecommerceProductsForConnection(ecommerceProducts: EcommerceProduct[], connectionId: number): EcommerceProduct[] {
    if ( !ecommerceProducts ) {
      return [];
    }
    return ecommerceProducts.filter( ecommerceProduct => Number(ecommerceProduct.ecommerceconnection_ID) === Number(connectionId) );
  }
  formatDate(date: Date | undefined): string | null {
    return date ? this.datePipe.transform(date, 'MMM d, yyyy at h:mm a') : null;
  }

  startQuantityEdit(item: Item): void {
    this.loadProductsAndEcommerceProducts();
    this.editingQuantity = item;
    this.itemQuantityForm.setValue(item.quantity);
    this.editingOrder.emit(true);
  }

  saveQuantityEdit(item: Item): void {
    if (this.itemQuantityForm.valid) {
      const newQuantity = this.itemQuantityForm.value as number;
      if ( newQuantity && item.quantity !== newQuantity ) {
        if ( !item['initialQuantity'] ) {
          item['initialQuantity'] = item.quantity;
        }
        item.quantity = newQuantity;
        this.updateEcommerceOrderAndComputeIfReady();
      }
    }
    this.editingQuantity = null;
    this.editingOrder.emit(false);
  }

  cancelQuantityEdit(): void {
    this.editingQuantity = null;
    this.editingOrder.emit(false);
  }

  get isQuantityEditDirty(): boolean {
    return this.editingQuantity && this.itemQuantityForm.value !== this.editingQuantity.quantity;
  }

  addItem() {
    this.loadProductsAndEcommerceProducts();
    // Sampled before the push below, for the same reason as in restoreItem():
    // adding a line to an order whose every line was dismissed revives it, and
    // the order-level ignore has to come off or the order stays hidden from
    // every filter once the operator saves.
    const hadNoActiveItems = activeOrderRequestItems(this.items).length === 0;
    let maxCurrentAddedItemId = 1;
    if ( this.ecommerceOrder.bookorder ) {
      for ( const currentItem of this.ecommerceOrder.bookorder.items ) {
        if (currentItem.source === 'ADDED_MANUALLY') {
          maxCurrentAddedItemId = Number(currentItem.remoteProductId) + 1;
        }

      }
    }
    const item: Item = { remoteProductId: '' + maxCurrentAddedItemId, ecommerceProductId: 0, source: 'ADDED_MANUALLY', ecommerceProduct: undefined, productId: 0, product: undefined, status: 'ERROR', quantity: 1, title: '' };
    this.editingProduct = item;
    this.ecommerceOrder.bookorder.items.push(item);
    this.refreshItemsForTable();
    this.itemQuantityForm.setValue(item.quantity);
    this.productSearchText = '';
    this.filteredProducts = this.products.slice();
    this.itemSelectionData = this.createItemSelectionData(item);
    if ( item.productId && !item.product ) {
      item.product = this.getProduct(item.productId);
    }
    if ( item.product ) {
      this.itemSelectionData.setProduct(item.product);
    }
    this.unignoreOrderIfRevived(hadNoActiveItems);
    this.editingOrder.emit(true);
  }

  /**
   * Re-point `bookorder.items` at a new array and resync `this.items`.
   *
   * `mat-table` given a plain array for `[dataSource]` reads it exactly once --
   * it wraps it in `observableOf(...)`, so a `push` or `splice` in place changes
   * nothing on screen. Manually added lines therefore did not appear until the
   * order was reopened, and deleted ones did not disappear. Replacing the array
   * reference is what makes the binding fire.
   *
   * The item objects themselves are reused, so `editingProduct` /
   * `editingQuantity` still identity-match their row after this runs.
   */
  private refreshItemsForTable(): void {
    if ( !this.ecommerceOrder || !this.ecommerceOrder.bookorder ) {
      return;
    }
    this.ecommerceOrder.bookorder.items = ( this.ecommerceOrder.bookorder.items || [] ).slice();
    this.items = this.ecommerceOrder.getItems();
  }

  /**
   * Mirror of the server's `isIgnoredOrderRequestItem` predicate, kept in
   * `orderRequestItemIgnore.ts` so the component, the readiness computation and
   * the template all judge "dismissed" the same way. Exposed as a method
   * because the template binds to it.
   */
  isIgnoredItem(item: any): boolean {
    return isIgnoredOrderRequestItem(item);
  }

  restoreItem(item: any) {
    if ( this.isIgnoredItem(item) ) {
      // Sampled BEFORE the mutation below. Used by unignoreOrderIfRevived() to
      // tell "this restore brought a fully-dismissed order back to life" apart
      // from "this order was ignored for some other reason and happens to have
      // a dismissed line" -- only the former may clear the order-level ignore.
      const hadNoActiveItems = activeOrderRequestItems(this.items).length === 0;

      // Put the original quantity back. The previous form of this had its two
      // branches swapped -- it assigned 1 when initialQuantity existed and
      // assigned initialQuantity (undefined) when it did not, with the guard
      // below silently rescuing the undefined. The net effect was that restore
      // ALWAYS produced 1, so restoring a dismissed line of 12 gave back a line
      // of 1 without any visible error.
      //
      // Restores from quantityBeforeIgnore, NOT initialQuantity. The two are
      // not the same thing: initialQuantity is the originally ORDERED quantity
      // (a documented field -- see docs/contracts/shared/PlaceOrder.md -- also
      // written by saveQuantityEdit for audit), whereas restore needs the
      // quantity at the moment of dismissal. For a line edited 12 -> 10 and
      // then dismissed, initialQuantity is 12 but the correct restore is 10.
      // initialQuantity is kept as the fallback so lines dismissed before this
      // field existed still restore to something sensible.
      if ( item['quantityBeforeIgnore'] ) {
        item.quantity = item['quantityBeforeIgnore'];
      } else if ( item['initialQuantity'] ) {
        item.quantity = item['initialQuantity'];
      }
      if ( !item.quantity || item.quantity <= 0 ) {
        item.quantity = 1;
      }

      // Clearing the status matters as much as the quantity: the server treats
      // 'IGNORE' as sufficient on its own, so a line restored to quantity 12
      // while still marked 'IGNORE' would keep being dropped from the order.
      if ( item.status === EcommerceOrderDetailsComponent.ITEM_STATUS_IGNORE ) {
        if ( item['statusBeforeIgnore'] ) {
          item.status = item['statusBeforeIgnore'];
        } else {
          // Dismissed before this field existed, so the pre-dismissal status is
          // unrecoverable. 'ERROR' forces the line back through resolution
          // rather than letting it claim a success nobody verified; the
          // compute() call below has the server re-evaluate it immediately.
          item.status = 'ERROR';
        }
      }
      // initialQuantity is deliberately NOT deleted -- it is the audit record
      // of what was originally ordered and outlives any dismiss/restore cycle.
      delete item['quantityBeforeIgnore'];
      delete item['statusBeforeIgnore'];

      this.unignoreOrderIfRevived(hadNoActiveItems);
      this.updateEcommerceOrderAndComputeIfReady();
    }
  }
  deleteItem(item: any) {
    if ( item.source && item.source === 'ADDED_MANUALLY' ) {
      // Filtered rather than spliced in a forward loop: splicing shifts every
      // later element down one while `i` still advances, so a run of matching
      // rows skipped every other one.
      this.ecommerceOrder.bookorder.items = this.ecommerceOrder.bookorder.items.filter(
        existingItem => !( existingItem.remoteProductId === item.remoteProductId && existingItem.title === item.title ) );
      this.refreshItemsForTable();
    } else {
      if ( !item['initialQuantity'] ) {
        item['initialQuantity'] = item.quantity;
      }
      // Snapshot for restore. Separate from initialQuantity on purpose -- see
      // the note in restoreItem(). Guarded so that dismissing an already
      // dismissed line cannot overwrite the snapshot with 0.
      if ( !item['quantityBeforeIgnore'] && item.quantity ) {
        item['quantityBeforeIgnore'] = item.quantity;
      }
      // Preserved so restoreItem() can genuinely undo rather than guess. The
      // status being replaced is a resolution result ('SUCCESS' / 'ERROR' /
      // 'ERROR_NO_REMOTE_PRODUCT_ID'), which is worth keeping -- a line
      // dismissed because it was unresolvable should come back unresolved.
      if ( !item['statusBeforeIgnore'] && item.status !== EcommerceOrderDetailsComponent.ITEM_STATUS_IGNORE ) {
        item['statusBeforeIgnore'] = item.status;
      }
      item.quantity = 0;
      // Both are sent. The quantity alone is enough for the current server
      // (its predicate is an OR, which is what clears orders dismissed before
      // 'IGNORE' existed), but the explicit status is the unambiguous
      // statement of operator intent and is what the server asked us to send.
      item.status = EcommerceOrderDetailsComponent.ITEM_STATUS_IGNORE;
    }
    this.ignoreOrderIfAllItemsDismissed();
    this.updateEcommerceOrderAndComputeIfReady();
  }

  // ---------------------------------------------------------------------------
  // Order-level ignoreStatus follows the lines
  //
  // An order with every line dismissed has nothing left to print. Rather than
  // leaving it in the working queue showing a generic product error -- which
  // reads as "something is wrong with a product" and sends an operator looking
  // in the wrong place -- the order itself is marked ignored.
  //
  // 'IGNORE' is existing, load-bearing vocabulary on this field, not something
  // invented here: EcommerceOrdersComponent already excludes ignored orders
  // from NEED_ATTENTION / READY_TO_RELEASE / PROCESSING / RELEASED / ON_HOLD
  // *and* from 'ALL', greys the row, and offers an 'IGNORED' filter plus an
  // un-ignore button. So an auto-ignored order drops out of the queue but stays
  // findable and recoverable without any new UI.
  //
  // Only deleteItem() and restoreItem() touch this. Product and quantity edits
  // deliberately do not: an operator who manually ignored an order must not
  // have that silently undone by an unrelated edit.
  // ---------------------------------------------------------------------------

  /**
   * Mark the whole order ignored once its last active line is dismissed.
   */
  private ignoreOrderIfAllItemsDismissed(): void {
    if ( !this.ecommerceOrder ) {
      return;
    }
    if ( activeOrderRequestItems(this.items).length > 0 ) {
      return;
    }
    if ( this.ecommerceOrder.ignoreStatus === 'IGNORE' ) {
      return;
    }
    console.log(`All lines dismissed on EcommerceOrder ${this.ecommerceOrder.ID} - setting ignoreStatus to IGNORE`);
    this.ecommerceOrder.ignoreStatus = 'IGNORE';
  }

  /**
   * Undo the above when a restore brings the order back to life.
   *
   * This is the counterpart that makes the auto-ignore safe. Without it, an
   * order auto-ignored above and then partially restored would keep
   * `ignoreStatus = 'IGNORE'` and stay hidden from every working filter
   * including 'ALL' -- the operator would restore a line, watch the order
   * vanish, and have no obvious way to work out why.
   *
   * Gated on the order having had NO active lines beforehand, so that this only
   * ever reverses an ignore the dismissal caused. An operator who manually
   * ignored an order that still has active lines keeps that decision, because
   * in that case `hadNoActiveItemsBefore` is false and nothing happens.
   */
  private unignoreOrderIfRevived(hadNoActiveItemsBefore: boolean): void {
    if ( !this.ecommerceOrder || !hadNoActiveItemsBefore ) {
      return;
    }
    if ( this.ecommerceOrder.ignoreStatus !== 'IGNORE' ) {
      return;
    }
    if ( activeOrderRequestItems(this.items).length === 0 ) {
      return;
    }
    console.log(`EcommerceOrder ${this.ecommerceOrder.ID} has an active line again - clearing ignoreStatus`);
    this.ecommerceOrder.ignoreStatus = 'NOT_IGNORE';
  }

  startProductEdit(item: Item): void {
    this.loadProductsAndEcommerceProducts();
    this.editingProduct = item;
    this.itemQuantityForm.setValue(item.quantity);
    this.productSearchText = '';
    this.filteredProducts = this.products.slice();
    this.itemSelectionData = this.createItemSelectionData(item);
    if ( item.productId && !item.product ) {
      item.product = this.getProduct(item.productId);
    }
    // A product already chosen on the line always wins over whatever the
    // matcher's ranking selected -- ProductCode auto-selects its best candidate
    // in the constructor, so without this an edit could silently re-point an
    // already-mapped line at a different product.
    if ( item.product ) {
      this.itemSelectionData.setProduct(item.product);
    }
    this.editingOrder.emit(true);
  }

  saveProductEdit(item: Item): void {
    // Record the choice as an EcommerceProduct only when the item was not created
    // from a CSV, was not added manually, the operator left the checkbox on, and
    // the connection actually resolves lines through EcommerceProducts.
    //
    // Only connections whose `productIdentifierType` is `ECOMMERCE_PRODUCT` resolve
    // order lines through an EcommerceProduct. In the three direct-to-Product modes
    // the "apply to future orders with the same remote product id" checkbox is
    // hidden by the template, so `saveProductSelectionToEcommerceProduct` is stuck
    // at its `true` default and the operator has no way to turn it off. Without the
    // mode check this method therefore wrote an EcommerceProduct for a connection
    // that never reads one, and the POST failed server-side on the duplicate
    // (ecommerceconnection_ID, product_ID, remoteproduct_ID) key. The mode check
    // has to come first -- the checkbox is only meaningful underneath it.
    // The server now also refuses this with a 409, but the rule is enforced here
    // so the operator never triggers a round trip that cannot succeed.
    if ( this.usesEcommerceProductMapping && this.saveProductSelectionToEcommerceProduct && !( item.source && ( item.source === 'CSV' || item.source === 'ADDED_MANUALLY') ) ) {
      // We have to have a remote productId in order to look for an EcommerceProduct by the remoteProductId
      if ( item.remoteProductId ) {
        let ecommerceProduct: EcommerceProduct | undefined;
        if ( item.ecommerceProductId ) {
          ecommerceProduct = this.ecommerceProductService.objectsById['' + item.ecommerceProductId];
          // Make sure it has a matching remoteProductId -- just data integrity checking -- this should never be possible
          if ( ecommerceProduct && String(ecommerceProduct.remoteproduct_ID) !== String(item.remoteProductId) ) {
            // This should never happen -- setting the ecommeceProduct back to nothing should result in fixing it.
            ecommerceProduct = undefined;
            item.ecommerceProductId = 0;
            item.ecommerceProduct = undefined;
          }
        }
        if ( !ecommerceProduct ) {
          ecommerceProduct = this.ecommerceProducts.find( obj => String(obj.remoteproduct_ID) === item.remoteProductId );
        }

        // If there is an existing EcommerceProduct with a matching remoteProductId then we are about to change it.
        if ( ecommerceProduct ) {
          if ( ecommerceProduct.product_ID !== this.itemSelectionData.product_ID ) {
            console.log(`Saving the changed product_ID in the EcommerceProduct in saveItemEdit `);
            ecommerceProduct.product_ID = this.itemSelectionData.product_ID;
            // `product_ID` is a Product id, so it has to be resolved against the
            // Product cache. This read the EcommerceProduct service's own id map,
            // which either returned nothing or -- when an EcommerceProduct simply
            // happened to carry that id -- attached an object of the wrong type
            // here and PUT it to the server.
            ecommerceProduct.product = this.getProduct(ecommerceProduct.product_ID);
            item.productId = this.itemSelectionData.product.ID;
            item.ecommerceProduct = ecommerceProduct;
            item.ecommerceProductId = ecommerceProduct.ID;
            item.remoteProductId = ecommerceProduct.remoteproduct_ID;
            if ( item.productId !== 0 ) {
              item.status = 'SUCCESS';
            } else {
              item.status = 'ERROR';
            }
            this.ecommerceProductService.updateItem(ecommerceProduct).subscribe( newEcommerceProduct => {
              item.ecommerceProductId = newEcommerceProduct.ID;
              item.ecommerceProduct = newEcommerceProduct;
              console.log(`Ecommerce Product updated in saveItemEdit`);
              this.updateEcommerceOrderAndComputeIfReady();
            });
          } else { // No change in the product_ID so nothing to recored -- this can happen when the productId on the item was saved separate from changing the EcommerceProduct's productId value and then the product is selected back
            if ( !item.productId && ecommerceProduct.product_ID !== 0) {
              item.productId = ecommerceProduct.product_ID;
              item.status = 'SUCCESS';
              this.updateEcommerceOrderAndComputeIfReady();
            }
            console.log(`No change in product_ID in saveItemEdit `);
          }
        } else { // Need to make a new EcommerceProduct
          ecommerceProduct = new EcommerceProduct(undefined);
          ecommerceProduct.product_ID = this.itemSelectionData.product_ID;
          ecommerceProduct.remoteproduct_ID = String(item.remoteProductId);
          ecommerceProduct.ecommerceconnection_ID = this.ecommerceOrder.ecommerceconnection_ID;
          // ecommerceProduct.product = this.ecommerceProductService.objectsById['' + item.product_ID];
          item.productId = this.itemSelectionData.product.ID;
          item.ecommerceProduct = ecommerceProduct;
          item.ecommerceProductId = ecommerceProduct.ID;
          this.ecommerceProductService.addItem(ecommerceProduct).subscribe( newEcommerceProduct => {
            if ( newEcommerceProduct ) {
              item.ecommerceProduct = newEcommerceProduct;
              item.ecommerceProductId = newEcommerceProduct.ID;
              item.status = 'SUCCESS';
              console.log(`New Ecommerce Product created in saveItemEdit`);
              this.updateEcommerceOrderAndComputeIfReady();
            }
          });
        }
      } else { // No remoteProductId error state unless this is a CSV order
        console.log(`Error there should be a remoteProductId`);
        item.status = 'ERROR_NO_REMOTE_PRODUCT_ID';
        this.updateEcommerceOrderAndComputeIfReady();
      }
    } else { // Either a CSV created item or a manually added item OR saveProductSelectionToEcommerceProduct is false
      if ( Number(item.productId) !== Number(this.itemSelectionData?.product?.ID) ) {
        // If there is a product selected the good
        if ( Number(this.itemSelectionData?.product?.ID) ) {
          item.status = 'SUCCESS';
          // if ( !item.title ) {
          //   item.title = this.itemSelectionData?.product?.title;
          // }
          item.productId = this.itemSelectionData.product.ID;
          item.product = this.itemSelectionData.product;
          this.updateEcommerceOrderAndComputeIfReady();
        } else { // If no product is selected then item is still in ERROR
          item.status = 'ERROR';
          item.productId = 0;
          item.product = undefined;
          this.updateEcommerceOrderAndComputeIfReady();
        }
      }
    }
    this.editingProduct = null;
    this.editingOrder.emit(false);
  }

  cancelProductEdit(): void {
    this.editingOrder.emit(false);
    this.editingProduct = null;
    this.editingOrder.emit(false);
  }

  get isProductEditDirty(): boolean {
    return this.editingProduct && this.itemSelectionData && this.itemSelectionData.product && this.editingProduct.productId !== this.itemSelectionData.product?.ID;
  }

  startEditCustomerDetails(): void {
    this.addressValidationOrder =  new AddressValidationOrder();
    // this.addressValidationOrder.addressValidationResult = [];

    // If there is an original adress set it as the original address
    if ( this.ecommerceOrder?.bookorder?.originalAddress && this.ecommerceOrder?.bookorder?.originalAddress.address1 ) {
      this.addressValidationOrder.title = this.ecommerceOrder.title;
      this.addressValidationOrder.firstName = this.ecommerceOrder?.bookorder?.originalAddress?.firstName;
      this.addressValidationOrder.lastName = this.ecommerceOrder?.bookorder?.originalAddress?.lastName;
      this.addressValidationOrder.company = this.ecommerceOrder?.bookorder?.originalAddress?.company;
      this.addressValidationOrder.address1 = this.ecommerceOrder?.bookorder?.originalAddress?.address1;
      this.addressValidationOrder.address2 = this.ecommerceOrder?.bookorder?.originalAddress?.address2;
      this.addressValidationOrder.city = this.ecommerceOrder?.bookorder?.originalAddress?.city;
      this.addressValidationOrder.postalCode = this.ecommerceOrder?.bookorder?.originalAddress?.postalcode;
      this.addressValidationOrder.region = this.ecommerceOrder?.bookorder?.originalAddress?.state;
      this.addressValidationOrder.country = this.ecommerceOrder?.bookorder?.originalAddress?.country;
      this.addressValidationOrder.email = this.ecommerceOrder?.bookorder?.originalAddress?.email;
      this.addressValidationOrder.telephone = this.ecommerceOrder?.bookorder?.originalAddress?.phone;
    } else {
      // If no original address then set the value of the original address to the current address
      this.addressValidationOrder.title = this.ecommerceOrder.title;
      this.addressValidationOrder.firstName = this.ecommerceOrder.firstname;
      this.addressValidationOrder.lastName = this.ecommerceOrder.lastname;
      this.addressValidationOrder.company = this.ecommerceOrder.company;
      this.addressValidationOrder.address1 = this.ecommerceOrder.address1;
      this.addressValidationOrder.address2 = this.ecommerceOrder.address2;
      this.addressValidationOrder.city = this.ecommerceOrder.city;
      this.addressValidationOrder.postalCode = this.ecommerceOrder.postalcode;
      this.addressValidationOrder.region = this.ecommerceOrder.state;
      this.addressValidationOrder.country = this.ecommerceOrder.country;
      this.addressValidationOrder.email = this.ecommerceOrder.email;
      this.addressValidationOrder.telephone = this.ecommerceOrder.phone;
    }

    // If the address is verified then set the currrent address to the verified address
    if ( this.ecommerceOrder.addressStatus === 'SUCCESS' ) {
      this.addressValidationOrder.addressToUse = 'VERIFIED';
      // this.addressValidationOrder.verifiedFirstName = this.ecommerceOrder.firstname;
      // this.addressValidationOrder.verifiedLastName = this.ecommerceOrder.firstname;
      // this.addressValidationOrder.verifiedCompany = this.ecommerceOrder.firstname;
      this.addressValidationOrder.verifiedAddress1 = this.ecommerceOrder.address1;
      this.addressValidationOrder.verifiedAddress2 = this.ecommerceOrder.address2;
      this.addressValidationOrder.verifiedCity = this.ecommerceOrder.city;
      this.addressValidationOrder.verifiedPostalCode = this.ecommerceOrder.postalcode;
      this.addressValidationOrder.verifiedRegion = this.ecommerceOrder.state;
      this.addressValidationOrder.verifiedCountry = this.ecommerceOrder.country;
    } else {
      this.addressValidationOrder.verifiedAddress1 = '';
      this.addressValidationOrder.verifiedAddress2 = '';
      this.addressValidationOrder.verifiedCity = '';
      this.addressValidationOrder.verifiedRegion = '';
      this.addressValidationOrder.verifiedPostalCode = '';
      this.addressValidationOrder.verifiedCountry = '';
    }

    // Set up the edited address to the current address
    this.addressValidationOrder.editedAddress1 = this.ecommerceOrder.address1;
    this.addressValidationOrder.editedAddress2 = this.ecommerceOrder.address2;
    this.addressValidationOrder.editedCity = this.ecommerceOrder.city;
    this.addressValidationOrder.editedPostalCode = this.ecommerceOrder.postalcode;
    this.addressValidationOrder.editedRegion = this.ecommerceOrder.state;
    this.addressValidationOrder.editedCountry = this.ecommerceOrder.country;

    // ...and the edited recipient to the current recipient. `nameEdited` stays
    // false until the operator actually changes something, so an untouched
    // order still saves exactly the name / company it arrived with.
    this.addressValidationOrder.editedTitle = this.addressValidationOrder.title;
    this.addressValidationOrder.editedFirstName = this.addressValidationOrder.firstName;
    this.addressValidationOrder.editedLastName = this.addressValidationOrder.lastName;
    this.addressValidationOrder.editedCompany = this.addressValidationOrder.company;

    // `originalAddress` is raw JSON with no defaults, so the block above can
    // have assigned undefined to any field the imported order did not carry.
    // Normalising here keeps undefined out of the template entirely.
    this.addressValidationOrder.normalizeStrings();

    this.customerEditMode = true;
    this.editingOrder.emit(true);
  }

  saveCustomerDetails(): void {
    if (this.ecommerceOrder) {
      if ( this.addressValidationOrder ) {
        // ---------------------------------------------------------------------
        // Why an edit here did not survive the reload (compute) button
        //
        // Three separate losses, all in this method:
        //
        // 1. The EDITED branch below only read two of its six fields. address1
        //    and address2 came from editedAddress1 / editedAddress2, but city,
        //    state, postcode and country were read from city / region /
        //    postalCode / country -- the IMPORTED values, not the edited ones.
        //    So correcting a misspelled city or a wrong ZIP appeared to work,
        //    saved the original, and looked for all the world like compute had
        //    reverted it. It had not: the edit never left this method. The
        //    ORIGINAL branch reads those same four fields, where it is correct,
        //    which is how the bug survived being looked at.
        //
        // 2. phone and email were wiped on every save. `address` is a fresh
        //    Address, whose phone/email default to '', and they were never
        //    assigned from the order before being copied back over
        //    ecommerceOrder.phone / .email and the bookorder's pair. Editing an
        //    address silently deleted the customer's contact details.
        //
        // 3. addressStatus was set to 'INITIAL' for EDITED and ORIGINAL -- the
        //    value meaning "nothing has decided about this address yet", which
        //    is an invitation to the next compute to decide from scratch. An
        //    address the operator has just chosen IS a decision.
        //
        // The gate below admits three things: an address the validator
        // confirmed; one in a country it cannot check at all; and one a
        // network-role operator has explicitly taken responsibility for after
        // validation came back with nothing usable. That third case exists
        // because "UPS found no candidates" is not proof an address is fake --
        // new construction and renumbered streets fail validation routinely --
        // and a permanent hold is not a defence against anything.
        //
        // What is still NOT admitted: an unvalidated address from an operator
        // without the network role, and any address flagged "Mark Address As
        // Invalid", which routes the order to the Address Issues queue.
        // ---------------------------------------------------------------------
        // The save button carries the same condition, so this is a backstop and
        // not the gate. Returning without clearing customerEditMode leaves the
        // operator in the editor with the reason still on screen.
        if ( !this.addressValidationOrder.canUseSelectedAddress() ) {
          console.log(`EcommerceOrder ${this.ecommerceOrder.ID} address not saved - ${this.addressValidationOrder.getBlockingReason()}`);
          return;
        }
        const address = new Address(undefined);
        // The recipient is not part of the address choice — the operator may
        // correct the name / company in the shared AddressComponent and then
        // pick any of the three address options — so it is read once, outside
        // the switch, from the "to use" accessors.
        address.firstname = this.addressValidationOrder.getFirstNameToUse();
        address.lastname = this.addressValidationOrder.getLastNameToUse();
        address.company = this.addressValidationOrder.getCompanyToUse();
        switch ( this.addressValidationOrder.addressToUse ) {
          case 'EDITED': {
            // Every one of these six reads `edited*`. Four of them used to read
            // the imported value instead, which is what made a city/state/ZIP
            // correction vanish.
            address.address1 = this.addressValidationOrder.editedAddress1;
            address.address2 = this.addressValidationOrder.editedAddress2;
            address.city = this.addressValidationOrder.editedCity;
            address.state = this.addressValidationOrder.editedRegion;
            address.postalcode = this.addressValidationOrder.editedPostalCode;
            address.country = this.addressValidationOrder.editedCountry;
            break;
          }
          case 'ORIGINAL': {
            address.address1 = this.addressValidationOrder.address1;
            address.address2 = this.addressValidationOrder.address2;
            address.city = this.addressValidationOrder.city;
            address.state = this.addressValidationOrder.region;
            address.postalcode = this.addressValidationOrder.postalCode;
            address.country = this.addressValidationOrder.country;
            break;
          }
          case 'VERIFIED': {
            address.address1 = this.addressValidationOrder.verifiedAddress1;
            address.address2 = this.addressValidationOrder.verifiedAddress2;
            address.city = this.addressValidationOrder.verifiedCity;
            address.state = this.addressValidationOrder.verifiedRegion;
            address.postalcode = this.addressValidationOrder.verifiedPostalCode;
            address.country = this.addressValidationOrder.verifiedCountry;
            this.ecommerceOrder.addressStatus = 'SUCCESS';
            break;
          }
        }
        // The gate has already established this address may ship. What is left
        // is to say so in a way `compute` will honour: only 'SUCCESS' and
        // 'MANUAL_OVERRIDE' stop it overwriting the address from the imported
        // one. 'INITIAL' would understate the decision to "undecided" -- which
        // is what caused the original report -- and 'ERROR' would permanently
        // park an international order that can never validate.
        this.ecommerceOrder.addressStatus = this.addressStatusForOperatorSave();

        // The audit trail for the line above, so "why is this order SUCCESS?"
        // stays answerable and the server can tell an operator override from a
        // clean validation. On the bookorder because that is the payload that
        // travels with the order.
        this.ecommerceOrder.bookorder.addressSetByOperator = true;
        this.ecommerceOrder.bookorder.addressValidationOutcome = this.addressValidationOrder.getAddressValidationOutcome();
        this.ecommerceOrder.bookorder.addressValidationOverride = !this.addressValidationOrder.isSelectedAddressValidated();

        // Contact details belong to the customer, not to any one of the three
        // address candidates, so they are carried across whichever was picked.
        // Without these two lines the blank defaults of `new Address(undefined)`
        // are what gets written back over the order.
        address.phone = this.addressValidationOrder.telephone ? this.addressValidationOrder.telephone : this.ecommerceOrder.phone;
        address.email = this.addressValidationOrder.email ? this.addressValidationOrder.email : this.ecommerceOrder.email;

        this.ecommerceOrder.title = this.addressValidationOrder.getTitleToUse();
        this.ecommerceOrder.firstname = address.firstname;
        this.ecommerceOrder.lastname = address.lastname;
        this.ecommerceOrder.company = address.company;
        this.ecommerceOrder.address1 = address.address1;
        this.ecommerceOrder.address2 = address.address2;
        this.ecommerceOrder.city = address.city;
        this.ecommerceOrder.state = address.state;
        this.ecommerceOrder.postalcode = address.postalcode;
        this.ecommerceOrder.country = address.country;
        // this.ecommerceOrder.phonecountry = address.phonecountry;
        this.ecommerceOrder.phone = address.phone;
        this.ecommerceOrder.email = address.email;
        this.ecommerceOrder.bookorder.title = this.ecommerceOrder.title;
        this.ecommerceOrder.bookorder.firstName = address.firstname;
        this.ecommerceOrder.bookorder.lastName = address.lastname;
        this.ecommerceOrder.bookorder.company = address.company;
        this.ecommerceOrder.bookorder.address1 = address.address1; // was a duplicate of address2, so address1 never updated here
        this.ecommerceOrder.bookorder.address2 = address.address2;
        this.ecommerceOrder.bookorder.city = address.city;
        this.ecommerceOrder.bookorder.state = address.state;
        this.ecommerceOrder.bookorder.postalcode = address.postalcode;
        this.ecommerceOrder.bookorder.country = address.country;
        // this.ecommerceOrder.bookorder.phoneCountry = address.phonecountry;
        this.ecommerceOrder.bookorder.phone = address.phone;
        this.ecommerceOrder.bookorder.email = address.email;
        // if ( this.currentAddress.validated ) {
        //   this.addressValidated = true;
        //   this.ecommerceOrder.addressStatus = 'SUCCESS';
        // } else { // Unvalidated address
        //   this.addressValidated = false;
        //   if ( this.ecommerceOrder.addressStatus === 'MANUAL_OVERRIDE' ) {
        //     // this.ecommerceOrder.addressStatus === 'MANUAL_OVERRIDE'
        //   } else {
        //     this.ecommerceOrder.addressStatus = 'INITIAL';
        //   }
        // }

      }



      // Update the ecommerceOrder object with the form values
      // this.ecommerceOrder.title = this.customerForm.value.title;
      // this.ecommerceOrder.firstname = this.customerForm.value.firstname;
      // this.ecommerceOrder.lastname = this.customerForm.value.lastname;
      // this.ecommerceOrder.company = this.customerForm.value.company;
      // this.ecommerceOrder.address1 = this.customerForm.value.address1;
      // this.ecommerceOrder.address2 = this.customerForm.value.address2;
      // this.ecommerceOrder.address3 = this.customerForm.value.address3;
      // this.ecommerceOrder.address4 = this.customerForm.value.address4;
      // this.ecommerceOrder.city = this.customerForm.value.city;
      // this.ecommerceOrder.state = this.customerForm.value.state;
      // this.ecommerceOrder.postalcode = this.customerForm.value.postalcode;
      // this.ecommerceOrder.country = this.customerForm.value.country;
      // this.ecommerceOrder.phonecountry = this.customerForm.value.phonecountry;
      // this.ecommerceOrder.phone = this.customerForm.value.phone;
      // this.ecommerceOrder.email = this.customerForm.value.email;
      // this.ecommerceOrder.bookorder.title = this.ecommerceOrder.title;
      // this.ecommerceOrder.bookorder.firstName = this.ecommerceOrder.firstname;
      // this.ecommerceOrder.bookorder.lastName = this.ecommerceOrder.lastname;
      // this.ecommerceOrder.bookorder.company = this.ecommerceOrder.company;
      // this.ecommerceOrder.bookorder.address2 = this.ecommerceOrder.address2;
      // this.ecommerceOrder.bookorder.address2 = this.ecommerceOrder.address2;
      // this.ecommerceOrder.bookorder.address3 = this.ecommerceOrder.address3;
      // this.ecommerceOrder.bookorder.address4 = this.ecommerceOrder.address4;
      // this.ecommerceOrder.bookorder.city = this.ecommerceOrder.city;
      // this.ecommerceOrder.bookorder.state = this.ecommerceOrder.state;
      // this.ecommerceOrder.bookorder.postalcode = this.ecommerceOrder.postalcode;
      // this.ecommerceOrder.bookorder.country = this.ecommerceOrder.country;
      // this.ecommerceOrder.bookorder.phoneCountry = this.ecommerceOrder.phonecountry;
      // this.ecommerceOrder.bookorder.phone = this.ecommerceOrder.phone;
      // this.ecommerceOrder.bookorder.email = this.ecommerceOrder.email;

      this.ecommerceOrder.ecommerceOrderStatus = 'VALID_RAW_ORDER';
      this.ecommerceOrderService.updateItem(this.ecommerceOrder).subscribe( updatedEcommerceOrder => {
        console.log('EcommerceOrder customer change save.');
        // Falls back to the local order: updateItem() routes failures through
        // handleErrorX, which emits undefined, and compute(undefined) would
        // have posted a body of "undefined" and lost the edit for real.
        this.ecommerceOrderService.compute(updatedEcommerceOrder ? updatedEcommerceOrder : this.ecommerceOrder).subscribe( reverifiedEcommerceOrder => {
          console.log(`EcommerceOrder has been reverified after the address was updated`);
        });
      });
    }
    this.customerEditMode = false;
    this.editingOrder.emit(false);
  }


  /**
   * The `addressStatus` to persist after an operator has chosen an address.
   *
   * Only two values here let an order progress server-side -- `SUCCESS` and
   * `MANUAL_OVERRIDE` -- and both stop `compute` from overwriting the address
   * with the imported one. The choice between them is a statement about WHO
   * stands behind the address, not about whether it may ship.
   */
  private addressStatusForOperatorSave(): string {
    if ( this.addressValidationOrder && this.addressValidationOrder.requiresManualOverride() ) {
      if ( EcommerceOrderDetailsComponent.MANUAL_OVERRIDE_ADDRESS_STATUS_AVAILABLE ) {
        return 'MANUAL_OVERRIDE';
      }
      // Overstated, knowingly, until the column accepts the honest value. The
      // audit fields on the bookorder carry the truth in the meantime.
      return 'SUCCESS';
    }
    return 'SUCCESS';
  }

  cancelCustomerEdit(): void {
    if (this.ecommerceOrder) {
      this.customerForm.patchValue({
        title: this.ecommerceOrder.title,
        firstname: this.ecommerceOrder.firstname,
        lastname: this.ecommerceOrder.lastname,
        company: this.ecommerceOrder.company,
        address1: this.ecommerceOrder.address1,
        address2: this.ecommerceOrder.address2,
        address3: this.ecommerceOrder.address3,
        address4: this.ecommerceOrder.address4,
        city: this.ecommerceOrder.city,
        state: this.ecommerceOrder.state,
        postalcode: this.ecommerceOrder.postalcode,
        country: this.ecommerceOrder.country,
        phonecountry: this.ecommerceOrder.phonecountry,
        phone: this.ecommerceOrder.phone,
        email: this.ecommerceOrder.email
      });
    }
    this.customerEditMode = false;
    this.editingOrder.emit(false);
  }


  startEditCarrierService(): void {
    this.originalCarrierService = this.ecommerceOrder.shippingInfo;
    this.rebuildCarrierServiceLevelOptions();
    // Re-seeded from the order, not left at whatever the last edit put there.
    // `setup()` only runs on init, so after a save (or a `compute()` that moved
    // the order on) the control still held the previous session's value, and
    // the box would open showing something the order no longer says.
    this.carrierServiceForm.setValue(this.originalCarrierService);
    this.carrierServiceEditMode = true;
    this.editingOrder.emit(true);
  }

  /**
   * Fill the Shipping combo box.
   *
   * Everything interesting is in `buildCarrierServiceLevelOptions()` in
   * `util/carrier-service-level-options.ts` -- in particular the rule that the
   * list is never empty, which is what makes an order whose incoming
   * carrier/service level we could not read fixable at all.
   *
   * Rebuilt on every `startEditCarrierService()` rather than once in `setup()`,
   * because `compute()` can replace `facilityOptions` and
   * `bookorder.facilityId` underneath us between one edit and the next.
   */
  private rebuildCarrierServiceLevelOptions(): void {
    if ( !this.ecommerceOrder ) {
      this.carrierServiceLevelOptions = [];
      this.carrierServiceLevelOptionsSource = 'CATALOG';
      return;
    }
    const built = buildCarrierServiceLevelOptions(
      this.ecommerceOrder.facilityOptions,
      this.ecommerceOrder.bookorder ? this.ecommerceOrder.bookorder.facilityId : 0,
      this.ecommerceOrder.shippingInfo );
    this.carrierServiceLevelOptions = built.options;
    this.carrierServiceLevelOptionsSource = built.source;
  }

  /**
   * The option the control currently names, for the mat-select trigger.
   *
   * Always resolves now that the incoming value is guaranteed an entry, which
   * is what stops the box reading as empty when the store sent something we
   * could not understand: MatSelect renders its trigger from the selected
   * *option*, and treats a value matching none as no selection at all.
   */
  getSelectedCarrierServiceLevelOption(): CarrierServiceLevelOption | undefined {
    const value = this.carrierServiceForm.value;
    if ( !value ) {
      return undefined;
    }
    return this.carrierServiceLevelOptions.find( option => option.id === value );
  }

  /**
   * Whether the Save button may be pressed.
   *
   * Unchanged is still not saveable, and neither is the unrecognized entry: it
   * is in the list to be read, not to be chosen, and committing it would write
   * the unreadable value straight back while claiming
   * `carrierServiceLevelStatus = 'SUCCESS'`.
   */
  canSaveCarrierService(): boolean {
    const value = this.carrierServiceForm.value;
    if ( !value || value === this.originalCarrierService ) {
      return false;
    }
    const option = this.getSelectedCarrierServiceLevelOption();
    return !!option && !option.unrecognized;
  }

  saveCarrierService(): void {
    if (this.ecommerceOrder) {
      this.ecommerceOrder.shippingInfo = this.carrierServiceForm.value;
      this.ecommerceOrder.bookorder.shippingInfo = this.carrierServiceForm.value;
      const carrierServiceLevelOption = this.getSelectedCarrierServiceLevelOption();
      // Only a priced choice may set the cost. The unpriced cases -- catalogue
      // entries for an order that was never rated -- leave the order's existing
      // figure alone for the `compute()` below to settle on the server. The old
      // `Number.MAX_VALUE` in this branch was written off as "should not be
      // possible"; now that the box offers unpriced entries by design it very
      // much is, and MAX_VALUE would fail the cost-vs-revenue gate on every
      // order an operator had just rescued -- the same dead end by another
      // route.
      if ( carrierServiceLevelOption && carrierServiceLevelOption.costKnown ) {
        this.ecommerceOrder.bookorder.shippingCost = carrierServiceLevelOption.cost;
        this.ecommerceOrder.shippingCost = carrierServiceLevelOption.cost;
      }
      this.ecommerceOrder.carrierServiceLevelStatus = 'SUCCESS';
      this.ecommerceOrder.ecommerceOrderStatus = 'VALID_RAW_ORDER';
      this.ecommerceOrderService.updateItem(this.ecommerceOrder).subscribe( updatedEcommerceOrder => {
        console.log('EcommerceOrder carrier service level change save.');
        this.ecommerceOrderService.compute(updatedEcommerceOrder).subscribe( reverifiedEcommerceOrder => {
          console.log(`EcommerceOrder has been reverified after product info was updated`);
        });
      });
    }
    this.carrierServiceEditMode = false;
    this.editingOrder.emit(false);
  }

  cancelCarrierServiceEdit(): void {
    this.carrierServiceForm.setValue(this.originalCarrierService);
    this.carrierServiceEditMode = false;
    this.editingOrder.emit(false);
  }

  updateEcommerceOrderAndComputeIfReady() {
    // Readiness is judged on the ACTIVE lines only. Without the isIgnoredItem()
    // skip, dismissing a line would be self-defeating: the dismissed line keeps
    // a non-'SUCCESS' status ('ERROR' for the unresolvable line that prompted
    // the dismissal, or 'IGNORE' now that we set it), so this loop would hold
    // productStatus at 'INITIAL' forever and the order could never proceed --
    // which is the exact problem dismissing a line is meant to solve.
    // This mirrors the server's `activeOrderRequestItems` filter.
    let activeItemCount = 0;
    let allItemsSuccess = true;
    for ( const i of this.items ) {
      if ( this.isIgnoredItem(i) ) {
        continue;
      }
      activeItemCount++;
      if ( i.status !== 'SUCCESS') {
        allItemsSuccess = false;
      }
    }
    // Every line dismissed is not "ready" -- it is an empty order. The order
    // itself is separately marked ignoreStatus = 'IGNORE' by
    // ignoreOrderIfAllItemsDismissed(), which is what actually takes it out of
    // the operator's queue; productStatus is left at 'INITIAL' rather than
    // guessed at 'ERROR' because the compute() call below has the server settle
    // the real value (it holds such orders at productStatus = 'ERROR').
    if ( allItemsSuccess && activeItemCount > 0 ) {
      this.ecommerceOrder.productStatus = 'SUCCESS';
    } else {
      this.ecommerceOrder.productStatus = 'INITIAL';
    }

    this.ecommerceOrder.ecommerceOrderStatus = 'VALID_RAW_ORDER';
    this.ecommerceOrder.releaseStatus = 'NOT_READY';
    // TODO: Confirm that the item changes are recorded in the this.ecommerceOrder
    const xxx = (this.ecommerceOrder as any).toJSON();
    this.ecommerceOrderService.updateItem(this.ecommerceOrder).subscribe( updatedEcommerceOrder => {
      console.log(`EcommerceOrder updated with new ecommerceProductInfo in the bookorder`);
      if ( updatedEcommerceOrder ) {
        this.ecommerceOrderService.compute(updatedEcommerceOrder).subscribe( reverifiedEcommerceOrder => {
          console.log(`EcommerceOrder has been reverified after product info was updated`);
        });
      } else {
        console.log(`EcommerceOrder did not return an EcommerceOrder and is not going to be reverified`);
      }
    });
  }

  relase(element: any) {
    if (this.ecommerceOrder) {
      element.classList.add('spinner');
      this.ecommerceOrder.userHoldStatus = 'NO_HOLD'; // Should this be possible -- should we prevent the release button if there is a hold?
      this.ecommerceOrder.releaseStatus = 'RELEASED';
      this.ecommerceOrderService.release(this.ecommerceOrder).subscribe( e => {
        element.classList.remove('spinner');
        console.log('release');
      });
    }
  }

  verifyAllProductsAreMapped() {
    // TODO: Check all items to see if they are all in SUCCESS status
  }

  getColor(intensity: number) {
    if ( intensity === 1.0) {
      return 'white';
    }
    if ( intensity > 0.6 ) {
      return 'black';
    }
    if ( intensity > 0.3 ) {
      return 'black';
    }
    return 'black';
  }

  getBackgroundColor(intensity: number) {
    if ( intensity === 1.0) {
      return 'rgb(0, 128, 0)';
    }
    if ( intensity > 0.6 ) {
      return 'rgb(240, 240, 0)';
    }
    if ( intensity > 0.3 ) {
      return 'rgb(240, 128, 0)';
    }
    return 'rgb(240, 0, 0)';
  }
  // ---------------------------------------------------------------------------
  // Product identification
  //
  // Which Product field an incoming order line is expected to equal is decided
  // by `EcommerceConnection.productIdentifierType` -- see
  // docs/contracts/shared/EcommerceConnection.md. These accessors are the only
  // place that field is interpreted, so the suggestion list, the code shown
  // beside each product and the EcommerceProduct-specific UI can never disagree
  // about it.
  // ---------------------------------------------------------------------------

  /**
   * `productIdentifierType` with the server's normalisation applied: `NULL`,
   * `''` and anything unrecognised mean `'ECOMMERCE_PRODUCT'`. Resolved in one
   * place rather than at each call site, exactly as the contract asks.
   */
  get effectiveProductIdentifierType(): string {
    const type = this.ecommerceOrder && this.ecommerceOrder.ecommerceConnection
      ? this.ecommerceOrder.ecommerceConnection.productIdentifierType
      : null;
    if ( type === 'CUSTOMER_PRODUCT_ID' || type === 'EAN13' || type === 'INTERNAL_PRODUCT_ID' ) {
      return type;
    }
    return 'ECOMMERCE_PRODUCT';
  }

  /**
   * True only for connections that resolve lines through an `EcommerceProduct`.
   *
   * The three direct-to-Product modes leave `item.ecommerceProductId` at 0, so
   * for them the "apply to future orders with the same remote product id"
   * checkbox and the "Not using an standard ecommerce product" notice are both
   * meaningless -- the template hides them on this.
   */
  get usesEcommerceProductMapping(): boolean {
    return this.effectiveProductIdentifierType === 'ECOMMERCE_PRODUCT';
  }

  /**
   * Resolve a `productId` to the Product itself.
   *
   * Prefers the service's id map, then falls back to the catalog this component
   * loaded -- `getFilteredItems` results are not guaranteed to be in
   * `objectsById` yet, and a row whose product is missing from the map used to
   * render a bare numeric id with no title.
   */
  getProduct(productId: number | string | undefined | null): Product | undefined {
    if ( !productId ) {
      return undefined;
    }
    const key = '' + productId;
    if ( this.productById[key] ) {
      return this.productById[key];
    }
    let product: Product = this.productService.objectsById[key];
    if ( !product && this.products && this.products.length > 0 ) {
      product = this.products.find(candidate => String(candidate.ID) === key);
    }
    if ( product ) {
      this.productById[key] = product;
    }
    return product;
  }

  /**
   * The customer-facing code to show beside a product, chosen to match the
   * field this connection actually identifies products by. Showing the wrong
   * one is worse than showing none: the operator is being asked to confirm a
   * mapping, and the code is the evidence.
   *
   * `INTERNAL_PRODUCT_ID` deliberately falls back to `customerProduct_ID`
   * because the internal id is already rendered on its own line, and repeating
   * it adds nothing.
   */
  getCustomerProductId(product: Product | undefined | null): string {
    if ( !product ) {
      return '';
    }
    let value: any;
    switch ( this.effectiveProductIdentifierType ) {
      case 'EAN13':
        value = product.EAN13;
        break;
      default:
        value = product.customerProduct_ID;
        break;
    }
    if ( value === undefined || value === null ) {
      return '';
    }
    const text = String(value).trim();
    // '0' is what an unset numeric column arrives as; it is not an identifier.
    return ( text.length === 0 || text === '0' ) ? '' : text;
  }

  /**
   * Tell the matcher which Product field this connection's incoming code is
   * supposed to equal, mirroring the server's resolution table. Returns null
   * for `ECOMMERCE_PRODUCT`, where the code is matched against an
   * `EcommerceProduct` rather than against any field of the Product, so there
   * is nothing to prefer and the matcher falls back to token overlap.
   */
  private getProductMatchPreference(item: Item): ProductMatchPreference | undefined {
    switch ( this.effectiveProductIdentifierType ) {
      case 'CUSTOMER_PRODUCT_ID':
        return item.sku ? { field: 'customerProduct_ID', term: String(item.sku) } : undefined;
      case 'EAN13':
        return item.sku ? { field: 'EAN13', term: String(item.sku) } : undefined;
      case 'INTERNAL_PRODUCT_ID':
        return item.remoteProductId ? { field: 'ID', term: String(item.remoteProductId) } : undefined;
      default:
        return undefined;
    }
  }

  /**
   * Build the match/selection state for one order line.
   *
   * Both incoming identifiers are handed to the matcher, not just the title:
   * an ecommerce line carries a `sku` as well, and scoring on the title alone
   * is what made the suggestions useless for catalogs whose titles are generic.
   */
  private createItemSelectionData(item: Item): ProductCode {
    const preference = this.getProductMatchPreference(item);
    const terms: Array<string | null | undefined> = [];
    if ( preference ) {
      terms.push(preference.term);
    }
    terms.push(item.title);
    terms.push(item.sku);
    // ProductCode trims, drops blanks and de-duplicates, so passing all three
    // unconditionally is safe.
    return new ProductCode(terms, this.products, preference);
  }

  /**
   * Reset the picker when the dropdown opens.
   *
   * The panel is destroyed and rebuilt each time it opens, so a stale filter
   * from the previous line would otherwise persist and hide most of the
   * catalog with no visible reason.
   */
  onProductSelectOpened(opened: boolean): void {
    if ( !opened ) {
      return;
    }
    this.productSearchText = '';
    this.filteredProducts = this.products.slice();
    // Deferred: the overlay content is not in the DOM until after this event.
    setTimeout(() => {
      const input = this.searchInput && this.searchInput.nativeElement ? this.searchInput.nativeElement : this.searchInput;
      if ( input && typeof input.focus === 'function' ) {
        input.focus();
      }
    });
  }

  /**
   * Keep keystrokes in the search box out of mat-select's own key handling.
   *
   * Without this, typing drives the select's typeahead (jumping the highlight
   * around), space selects an option and the arrow keys move the highlight
   * instead of the caret. Escape is deliberately allowed through so the panel
   * still closes on it.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    if ( event.key === 'Escape' || event.key === 'Esc' ) {
      return;
    }
    event.stopPropagation();
  }

  onSearch(value: string) {
    this.productSearchText = value === undefined || value === null ? '' : value;
    const filterValue = this.productSearchText.toLowerCase().trim();
    if ( filterValue.length === 0 ) {
      this.filteredProducts = this.products.slice();
      return;
    }
    // Titles can be null on a partially-populated Product, which used to throw
    // out of this filter on the first keystroke.
    this.filteredProducts = this.products.filter(product =>
      ( product.title ? product.title.toLowerCase() : '' ).includes(filterValue)
      || this.getCustomerProductId(product).toLowerCase().includes(filterValue)
      || String(product.ID) === filterValue
    );
  }

  clearSearch(event: Event) {
    event.stopPropagation(); // Prevents the dropdown from closing
    this.onSearch('');
  }

  toggleSaveProductSelectionToEcommerceProduct() {
    this.saveProductSelectionToEcommerceProduct = !this.saveProductSelectionToEcommerceProduct;
  }
}

